import { useMemo, useRef, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-aria-components';
import type { StockAdminQueue } from '@myrewards/contracts';
import { TwoColumns } from '../shell';
import {
  Card,
  cx,
  DataTable,
  EmptyState,
  FilterBar,
  formatCount,
  formatDate,
  formatMoney,
  plural,
  StatusPill,
  type DataColumn,
} from '../ui';
import { fiscalYearOn } from '../ui/fiscal';
import { ClaimButton } from '../ops/queue/ClaimButton';
import {
  ageWords,
  claimApplies,
  rowHref,
  stepLabel,
  stepTone,
  waitingOnWords,
} from '../ops/queue/presenter';
import { NoMatches } from '../ops/queue/NoMatches';
import { queueSorts, type QueueFilterId } from '../ops/queue/sorting';
import { useQueueTable } from '../ops/queue/useQueueTable';
import { QueueViews } from '../ops/queue/QueueViews';
import { TodayCard } from '../ops/queue/TodayCard';
import { WhoIsWorkingCard } from '../ops/queue/WhoIsWorkingCard';
import { AWARD_TYPE_SHORT } from './labels';
import { ReadinessCard } from './ReadinessCard';
import { ValuesInUseCard } from './ValuesInUseCard';
import s from './Home.module.css';

type Row = StockAdminQueue['rows'][number];

/** Every filter means something on Home: it holds every active stage (D4). */
const FILTERS: readonly QueueFilterId[] = ['claim', 'stage', 'type', 'pool', 'submitted'];

/**
 * The Home queue's columns (D1): Submitted second, with how long it has been with the team under
 * the date, so the date shows without the sideways scroll and the table gets no wider. Each column
 * that means something to sort by sorts (D3).
 */
function columns(meId: string, asOf: string): DataColumn<Row>[] {
  const sort = queueSorts(meId, asOf);
  return [
    {
      id: 'award',
      header: 'Award',
      cell: (row) => (
        <>
          <Link href={rowHref(row)} className={s.strongLink}>
            {row.award.displayId}
          </Link>
          <span className={s.subId}>{row.subject}</span>
        </>
      ),
      nowrap: true,
      card: 'title',
      sort: sort.award,
    },
    {
      id: 'submitted',
      header: 'Submitted',
      cell: (row) => (
        <>
          {formatDate(row.submittedAt)}
          <span className={s.subId}>{ageWords(row.since, asOf)}</span>
        </>
      ),
      nowrap: true,
      sort: sort.submitted,
    },
    {
      id: 'stage',
      header: 'Stage',
      cell: (row) => (
        <>
          <StatusPill tone={stepTone(row)} label={stepLabel(row)} />
          {waitingOnWords(row) !== null && <span className={s.subId}>{waitingOnWords(row)}</span>}
        </>
      ),
      sort: sort.stage,
    },
    {
      id: 'claim',
      header: 'Claim',
      card: 'action',
      cell: (row) =>
        row.claimedBy ? (
          <>
            <span className={s.subId}>Claimed by</span>
            <span className={s.claimer}>
              <span className={s.claimerAvatar} aria-hidden="true">
                {row.claimedBy.initials}
              </span>
              {row.claimedBy.name}
            </span>
          </>
        ) : claimApplies(row) ? (
          <ClaimButton
            awardId={row.award.id}
            displayId={row.award.displayId}
            version={row.version}
            to={rowHref(row)}
          />
        ) : (
          <span className={s.muted}>No claim at this step</span>
        ),
      nowrap: true,
      sort: sort.claim,
    },
    { id: 'type', header: 'Type', cell: (row) => AWARD_TYPE_SHORT[row.type], sort: sort.type },
    { id: 'pool', header: 'Sector / pool', cell: (row) => row.pool, sort: sort.pool },
    {
      id: 'people',
      header: 'People',
      cell: (row) => formatCount(row.people),
      numeric: true,
      sort: sort.people,
    },
    {
      id: 'cash',
      header: 'Cash',
      cell: (row) => formatMoney(row.cashCents),
      numeric: true,
      sort: sort.cash,
    },
    {
      id: 'options',
      header: 'Options',
      cell: (row) =>
        row.options === null ? (
          <span className={s.muted}>Cash only</span>
        ) : (
          formatCount(row.options)
        ),
      numeric: true,
      sort: sort.options,
    },
  ];
}

function intro(queue: StockAdminQueue): string {
  const active = queue.rows.length;
  if (active === 0)
    return 'Submitted awards stay here through their active stages. Claims show to the whole team.';
  const waiting = queue.rows.filter((row) => row.claimedBy === null && claimApplies(row)).length;
  const parts = [`${plural(active, 'active award', 'active awards')} across all stages.`];
  if (waiting > 0) parts.push(`${plural(waiting, 'award is', 'awards are')} waiting for a claim.`);
  const claimed = queue.rows.filter((row) => row.claimedBy !== null).length;
  if (claimed > 0) parts.push(`${plural(claimed, 'award is', 'awards are')} claimed.`);
  return parts.join(' ');
}

/**
 * An empty queue on day one ("nothing has reached it yet") or caught up ("All caught up"): two
 * messages with two kinds of art (DESIGN-SPEC §5). Day one when no award has ever reached the
 * team, or in the design-review first-run view. When it can't be told, the table's own empty row
 * says enough.
 */
function EmptyQueue({
  queueEmail,
  history,
}: {
  queueEmail: StockAdminQueue['queueEmail'];
  history: boolean;
}): ReactElement {
  // APP-092: says how a new award reaches you only as it does: an email, the digest, or nothing.
  const first =
    queueEmail === 'per_request'
      ? ' You get an email when the first one arrives.'
      : queueEmail === 'digest'
        ? ' Your daily digest counts each new one.'
        : '';
  const next =
    queueEmail === 'per_request'
      ? ', and you get an email.'
      : queueEmail === 'digest'
        ? ', and your daily digest counts them.'
        : '.';
  return !history ? (
    <EmptyState
      variant="first-day"
      art="calendar"
      size="sm"
      headline="Nothing in the queue yet"
      body={`Submitted awards appear here from approval through Shareworks. Approved awards are ready for the team to claim.${first}`}
    />
  ) : (
    <EmptyState
      variant="caught-up"
      size="sm"
      headline="All caught up"
      body={`There are no active submitted awards. New approved awards arrive ready to claim${next}`}
    />
  );
}

/** HOME2: for the Stock Admin team the work queue is Home; its views live in the left sidebar. */
export function QueueHome({
  queue,
  meId,
  asOf,
  waiting,
  others,
  pools,
  history,
  spot,
}: {
  queue: StockAdminQueue;
  /** Who is reading: "Claimed by me" is their claims. */
  meId: string;
  asOf: string;
  waiting: ReactNode;
  /** APP-148 D2: Waiting on others, under Waiting on you, while a switch is on. */
  others?: ReactNode;
  /** APP-148 R82: Budget pools, under Waiting on others (the Home board). */
  pools?: ReactNode;
  /** Whether any award has ever reached the team; `null` when it can't be told. */
  history: boolean | null;
  /** APP-232: Spot awards in progress, under Budget pools. */
  spot?: ReactNode;
}): ReactElement {
  const cols = useMemo(() => columns(meId, asOf), [meId, asOf]);
  const barRef = useRef<HTMLDivElement>(null);
  // D3/D4: sorted and filtered in the address, oldest first by default, over the whole list.
  const table = useQueueTable({
    table: 'home_queue',
    rows: queue.rows,
    columns: cols,
    filterIds: FILTERS,
    meId,
    asOf,
    barRef,
  });
  const empty = queue.rows.length === 0;
  return (
    <>
      {waiting}
      {others}
      {pools}
      {spot}
      <div className={s.queueLayout}>
        <QueueViews queue={queue} queueHref="/" />
        <div className={s.queueMain}>
          <ReadinessCard fiscalYear={fiscalYearOn(asOf)} firstDay={history === false} />
          <Card
            title="Queue"
            className={s.queueCard}
            aside={<span className={cx('eyebrow', s.meta)}>Requests</span>}
            help={{
              shows:
                'All active submitted awards, with their current stage and the name of anyone holding a claim.',
              canDo:
                'Claim an award when it reaches the check or letters. Claimed awards stay visible to the whole team. Open My queue or a stage to narrow the list.',
            }}
          >
            {/* Empty too: how the queue works sits above its one empty row (HOME2.empty). */}
            <p className={s.cardText}>{intro(queue)}</p>
            {/* Nothing to sort or filter in an empty queue: its own message stands alone. */}
            {!empty && (
              <div ref={barRef}>
                <FilterBar label="Filter the queue" {...table.bar} />
              </div>
            )}
            {/* On a phone the rows are cards (QA-042). Empty, the queue's own day-one or
                caught-up message is the table's one empty row: one message for one table. A
                filter that leaves nothing says so, with the way back. */}
            <DataTable
              caption="Queue"
              columns={cols}
              rows={table.rows}
              rowKey={(row) => row.award.id}
              minWidth={920}
              cards
              {...(!empty && { sort: table.sort, onSortChange: table.setSort })}
              empty={{
                title: 'No active awards right now.',
                body: 'Submitted awards stay here through approval, checks, letters, employee choice, Payroll and Shareworks.',
              }}
              {...(!empty && table.rows.length === 0
                ? { emptyState: <NoMatches onClear={table.clearFromEmpty} /> }
                : history === null
                  ? {}
                  : {
                      emptyState: <EmptyQueue queueEmail={queue.queueEmail} history={history} />,
                    })}
            />
          </Card>
          {/* SA1: today's activity beside who's working and the values in use. */}
          <TwoColumns
            sideLabel="The Stock Admin team"
            main={<TodayCard teammates={queue.teammates} />}
            side={
              <div className={s.queueSide}>
                <WhoIsWorkingCard teammates={queue.teammates} />
                <ValuesInUseCard />
              </div>
            }
          />
        </div>
      </div>
    </>
  );
}
